import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
    Tabs, Button, Tag, Progress, Modal, Form, Input, Select, 
    DatePicker, Checkbox, Popconfirm, message, Tooltip, Alert, Upload, Skeleton 
} from "antd";
import { 
    ArrowLeftOutlined, PlusOutlined, FileTextOutlined, 
    WarningOutlined, CalendarOutlined, CheckCircleOutlined, 
    ClockCircleOutlined, UploadOutlined, DeleteOutlined, 
    PrinterOutlined, UserOutlined, EditOutlined, CheckOutlined,
    CloseCircleOutlined, LockOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import LoadingSpinner from "../../common/LoadingSpinner";
import "./ClientOnboarding.css";

const API_URL = process.env.REACT_APP_API_URL || "";

const DELAY_CATEGORIES = [
    "Client IT / SSO / Firewall Access",
    "Contract / Legal Review Delay",
    "Data Formatting & Cleansing Issues",
    "Client Stakeholder Unavailability",
    "Scope / Package Revision",
    "Third-Party Integration Blocker",
    "Other"
];

const OnboardingDetail = () => {
    const { id: trackerId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [tracker, setTracker] = useState(null);
    const [activeTab, setActiveTab] = useState("checklist");
    const [selectedStageFilter, setSelectedStageFilter] = useState("all");

    // Modals
    const [isDelayModalOpen, setIsDelayModalOpen] = useState(false);
    const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
    const [approvalType, setApprovalType] = useState("go_live");

    // Forms
    const [delayForm] = Form.useForm();
    const [meetingForm] = Form.useForm();
    const [taskForm] = Form.useForm();
    const [docForm] = Form.useForm();
    const [approvalForm] = Form.useForm();
    const [fileList, setFileList] = useState([]);

    const fetchTracker = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}api/superadmin/onboarding/${trackerId}/`, { withCredentials: true });
            if (res.data?.success) {
                setTracker(res.data.tracker);
            } else {
                message.error(res.data?.message || "Failed to load tracker.");
            }
        } catch (err) {
            console.error("Error fetching tracker:", err);
            message.error(err.response?.data?.message || "Failed to load tracker details.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (trackerId) {
            fetchTracker();
        }
    }, [trackerId]);

    // Update Top-level health
    const handleHealthChange = async (newHealth) => {
        try {
            const res = await axios.put(`${API_URL}api/superadmin/onboarding/${trackerId}/`, {
                health: newHealth
            }, { withCredentials: true });
            if (res.data?.success) {
                setTracker(res.data.tracker);
                message.success(`Status updated to ${newHealth.replace('_', ' ')}`);
            }
        } catch (err) {
            message.error("Failed to update status.");
        }
    };

    // Toggle Task Completion
    const handleToggleTask = async (task) => {
        const nextState = !task.completed;
        try {
            const res = await axios.patch(
                `${API_URL}api/superadmin/onboarding/${trackerId}/tasks/${task.id}/`, 
                { completed: nextState }, 
                { withCredentials: true }
            );
            if (res.data?.success) {
                setTracker(prev => ({
                    ...prev,
                    tasks: res.data.tasks,
                    stages: res.data.stages || prev.stages,
                    overall_progress: res.data.overall_progress
                }));
            }
        } catch (err) {
            message.error("Failed to update task.");
        }
    };

    // Bulk Toggle All Tasks in View / Stage
    const handleToggleAll = async (checked) => {
        try {
            const stageId = selectedStageFilter === "all" ? 0 : parseInt(selectedStageFilter, 10);
            const res = await axios.post(
                `${API_URL}api/superadmin/onboarding/${trackerId}/stages/${stageId}/toggle-all/`,
                { completed: checked },
                { withCredentials: true }
            );
            if (res.data?.success) {
                setTracker(prev => ({
                    ...prev,
                    tasks: res.data.tasks,
                    stages: res.data.stages || prev.stages,
                    overall_progress: res.data.overall_progress
                }));
                message.success(checked ? "All tasks marked as completed!" : "All tasks marked as incomplete.");
            }
        } catch (err) {
            message.error("Failed to update tasks.");
        }
    };

    // Add Client-Specific Task
    const handleCreateTask = async (values) => {
        try {
            const payload = {
                title: values.title,
                stage_id: values.stage_id,
                owner: values.owner,
                due_date: values.due_date ? values.due_date.format("YYYY-MM-DD") : null,
                dependencies: values.dependencies || [],
                notes: values.notes || ""
            };
            const res = await axios.post(`${API_URL}api/superadmin/onboarding/${trackerId}/tasks/`, payload, { withCredentials: true });
            if (res.data?.success) {
                message.success("Client task added.");
                setIsTaskModalOpen(false);
                taskForm.resetFields();
                fetchTracker();
            }
        } catch (err) {
            message.error(err.response?.data?.message || "Failed to add task.");
        }
    };

    // Delete Custom Task
    const handleDeleteTask = async (taskId) => {
        try {
            const res = await axios.delete(`${API_URL}api/superadmin/onboarding/${trackerId}/tasks/${taskId}/`, { withCredentials: true });
            if (res.data?.success) {
                message.success("Task deleted.");
                fetchTracker();
            }
        } catch (err) {
            message.error("Failed to delete task.");
        }
    };

    // Update Stage Status
    const handleStageStatusChange = async (stageId, newStatus) => {
        try {
            const res = await axios.patch(
                `${API_URL}api/superadmin/onboarding/${trackerId}/stages/${stageId}/`,
                { status: newStatus },
                { withCredentials: true }
            );
            if (res.data?.success) {
                setTracker(prev => ({ ...prev, stages: res.data.stages }));
                message.success(`Stage ${stageId} marked as ${newStatus}`);
            }
        } catch (err) {
            message.error("Failed to update stage status.");
        }
    };

    // Add Delay Reason
    const handleCreateDelay = async (values) => {
        try {
            const payload = {
                stage_id: values.stage_id,
                reason_category: values.reason_category,
                description: values.description
            };
            const res = await axios.post(`${API_URL}api/superadmin/onboarding/${trackerId}/delays/`, payload, { withCredentials: true });
            if (res.data?.success) {
                message.success("Delay logged & status flagged.");
                setIsDelayModalOpen(false);
                delayForm.resetFields();
                fetchTracker();
            }
        } catch (err) {
            message.error("Failed to log delay.");
        }
    };

    // Resolve / Delete Delay
    const handleDeleteDelay = async (delayId) => {
        try {
            const res = await axios.delete(`${API_URL}api/superadmin/onboarding/${trackerId}/delays/${delayId}/`, { withCredentials: true });
            if (res.data?.success) {
                message.success("Delay resolved / removed.");
                fetchTracker();
            }
        } catch (err) {
            message.error("Failed to remove delay.");
        }
    };

    // Add Diary / Meeting Note
    const handleCreateMeeting = async (values) => {
        try {
            const payload = {
                title: values.title,
                date: values.date ? values.date.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
                attendees: values.attendees,
                notes: values.notes,
                action_items: values.action_items || ""
            };
            const res = await axios.post(`${API_URL}api/superadmin/onboarding/${trackerId}/meetings/`, payload, { withCredentials: true });
            if (res.data?.success) {
                message.success("Meeting note logged in diary.");
                setIsMeetingModalOpen(false);
                meetingForm.resetFields();
                fetchTracker();
            }
        } catch (err) {
            message.error("Failed to add meeting note.");
        }
    };

    // Delete Meeting Note
    const handleDeleteMeeting = async (noteId) => {
        try {
            const res = await axios.delete(`${API_URL}api/superadmin/onboarding/${trackerId}/meetings/${noteId}/`, { withCredentials: true });
            if (res.data?.success) {
                message.success("Meeting note removed.");
                fetchTracker();
            }
        } catch (err) {
            message.error("Failed to delete note.");
        }
    };

    // Upload Document
    const handleUploadDoc = async (values) => {
        try {
            const formData = new FormData();
            formData.append("title", values.title);
            formData.append("stage_id", values.stage_id || 1);
            formData.append("notes", values.notes || "");
            if (fileList.length > 0) {
                formData.append("file", fileList[0].originFileObj);
            }

            const res = await axios.post(`${API_URL}api/superadmin/onboarding/${trackerId}/documents/`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
                withCredentials: true
            });
            if (res.data?.success) {
                message.success("Document attached to onboarding repository.");
                setIsDocModalOpen(false);
                docForm.resetFields();
                setFileList([]);
                fetchTracker();
            }
        } catch (err) {
            message.error("Failed to upload document.");
        }
    };

    // Delete Document
    const handleDeleteDoc = async (docId) => {
        try {
            const res = await axios.delete(`${API_URL}api/superadmin/onboarding/${trackerId}/documents/${docId}/`, { withCredentials: true });
            if (res.data?.success) {
                message.success("Document removed.");
                fetchTracker();
            }
        } catch (err) {
            message.error("Failed to delete document.");
        }
    };

    // Submit Approval
    const handleSubmitApproval = async (values) => {
        try {
            const payload = {
                type: approvalType,
                approver_name: values.approver_name,
                remarks: values.remarks || ""
            };
            const res = await axios.post(`${API_URL}api/superadmin/onboarding/${trackerId}/approvals/`, payload, { withCredentials: true });
            if (res.data?.success) {
                message.success(`${approvalType === 'go_live' ? 'Go-Live' : 'Client Acceptance'} approved successfully!`);
                setIsApprovalModalOpen(false);
                approvalForm.resetFields();
                fetchTracker();
            }
        } catch (err) {
            message.error("Failed to record approval.");
        }
    };

    // Filtered Tasks
    const displayedTasks = useMemo(() => {
        if (!tracker || !tracker.tasks) return [];
        if (selectedStageFilter === "all") return tracker.tasks;
        return tracker.tasks.filter(t => t.stage_id === parseInt(selectedStageFilter, 10));
    }, [tracker, selectedStageFilter]);

    if (loading) {
        return (
            <div className="onb-container">
                {/* Header Skeleton Card */}
                <div className="onb-detail-header" style={{ marginBottom: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, paddingBottom: 10, borderBottom: "1px solid #f1f5f9" }}>
                        <Skeleton.Button active size="small" style={{ width: 150 }} />
                        <Skeleton.Button active size="small" style={{ width: 100 }} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
                        <div>
                            <Skeleton.Input active size="large" style={{ width: 280, height: 32, marginBottom: 8 }} />
                            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                                <Skeleton.Input active size="small" style={{ width: 120 }} />
                                <Skeleton.Input active size="small" style={{ width: 100 }} />
                                <Skeleton.Input active size="small" style={{ width: 140 }} />
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <Skeleton.Button active style={{ width: 110 }} />
                            <Skeleton.Button active style={{ width: 100 }} />
                            <Skeleton.Button active style={{ width: 120 }} />
                            <Skeleton.Button active style={{ width: 130 }} />
                        </div>
                    </div>
                    <Skeleton.Input active size="small" style={{ width: "100%", height: 10, borderRadius: 5 }} />
                </div>

                {/* 11-Stage Stepper Skeleton */}
                <div style={{ display: "flex", gap: 8, marginBottom: 20, overflowX: "hidden" }}>
                    {[...Array(8)].map((_, i) => (
                        <div key={i} style={{ flex: 1, background: "#ffffff", padding: "12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                            <Skeleton active paragraph={{ rows: 1 }} title={{ width: "60%" }} />
                        </div>
                    ))}
                </div>

                {/* Tabs & Tasks Skeleton Card */}
                <div style={{ background: "#ffffff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "24px" }}>
                    <div style={{ display: "flex", gap: 24, marginBottom: 20, borderBottom: "1px solid #f1f5f9", paddingBottom: 12 }}>
                        <Skeleton.Button active style={{ width: 150, height: 30 }} />
                        <Skeleton.Button active style={{ width: 120, height: 30 }} />
                        <Skeleton.Button active style={{ width: 120, height: 30 }} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        {[...Array(4)].map((_, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 18px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fafafa" }}>
                                <Skeleton.Avatar active size="small" shape="square" style={{ width: 18, height: 18 }} />
                                <div style={{ flex: 1 }}>
                                    <Skeleton.Input active size="small" style={{ width: "55%", height: 16, marginBottom: 6 }} />
                                    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                                        <Skeleton.Button active size="small" style={{ width: 90, height: 20 }} />
                                        <Skeleton.Button active size="small" style={{ width: 110, height: 20 }} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!tracker) {
        return (
            <div className="onb-container" style={{ textAlign: "center", padding: "80px 0" }}>
                <h2>Client Tracker Not Found</h2>
                <Link to="/superadmin_onboarding">
                    <Button type="primary">Back to Onboarding Hub</Button>
                </Link>
            </div>
        );
    }

    const progress = tracker.overall_progress || 0;
    const stages = tracker.stages || [];
    const delays = tracker.delays || [];
    const meetingNotes = tracker.meeting_notes || [];
    const documents = tracker.documents || [];
    const approvals = tracker.approvals || {};

    // Gating logic: Stages 1 through 9 must be completed before Go-Live can be approved
    const preFlightStages = stages.filter(s => s.id >= 1 && s.id <= 9);
    const pendingPreFlightStages = preFlightStages.filter(s => s.status !== "completed");
    const isReadyForGoLive = pendingPreFlightStages.length === 0;

    return (
        <div className="onb-container">
            {/* Header Card */}
            <div className="onb-detail-header">
                {/* Integrated Navigation Inside Card */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #f1f5f9" }}>
                    <Link to="/superadmin_onboarding" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#2563eb", fontWeight: 600, fontSize: 13, textDecoration: "none" }}>
                        <ArrowLeftOutlined /> Back to All Onboardings
                    </Link>
                    <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600, background: "#f1f5f9", padding: "2px 8px", borderRadius: 4 }}>
                        {tracker.tracker_code}
                    </span>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: "#0f172a" }}>
                                {tracker.client_name}
                            </h1>
                            <span className="onb-client-code">{tracker.tracker_code}</span>
                            <Select
                                value={tracker.health || "on_track"}
                                onChange={handleHealthChange}
                                size="small"
                                style={{ width: 140 }}
                                options={[
                                    { label: "🟢 On Track", value: "on_track" },
                                    { label: "🟡 At Risk", value: "at_risk" },
                                    { label: "🔴 Delayed", value: "delayed" }
                                ]}
                            />
                            {tracker.status === "go_live_approved" && <Tag color="success">Go-Live Approved</Tag>}
                            {tracker.status === "completed" && <Tag color="blue">Fully Completed</Tag>}
                        </div>

                        <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 13, color: "#64748b", flexWrap: "wrap" }}>
                            <span><strong>Legal Entity:</strong> {tracker.legal_entity_name}</span>
                            <span>•</span>
                            <span><strong>Industry:</strong> {tracker.industry}</span>
                            <span>•</span>
                            <span><strong>Lead Owner:</strong> {tracker.lead_owner}</span>
                            <span>•</span>
                            <span><strong>VRM:</strong> {tracker.vrm_assigned}</span>
                            <span>•</span>
                            <span><strong>Target Go-Live:</strong> {tracker.target_go_live_date || "Not set"}</span>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <Button 
                            icon={<FileTextOutlined />} 
                            onClick={() => setIsReportModalOpen(true)}
                        >
                            Status Report
                        </Button>
                        <Button 
                            danger 
                            icon={<WarningOutlined />} 
                            onClick={() => setIsDelayModalOpen(true)}
                        >
                            Log Delay
                        </Button>
                        <Button 
                            icon={<CalendarOutlined />} 
                            onClick={() => setIsMeetingModalOpen(true)}
                        >
                            Add Diary Note
                        </Button>
                        {/* 
                        <Button 
                            icon={<UploadOutlined />} 
                            onClick={() => setIsDocModalOpen(true)}
                        >
                            Attach Doc
                        </Button> 
                        */}
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined />} 
                            onClick={() => setIsTaskModalOpen(true)}
                            style={{ background: "#2563eb", borderColor: "#2563eb" }}
                        >
                            Add Custom Task
                        </Button>
                    </div>
                </div>

                {/* Overall Progress Bar */}
                <div style={{ marginTop: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
                        <span>Overall Implementation Progress</span>
                        <span>{progress}% ({tracker.completed_tasks || 0} of {tracker.total_tasks || 33} tasks done)</span>
                    </div>
                    <Progress 
                        percent={progress} 
                        strokeColor={progress === 100 ? "#16a34a" : (tracker.health === "delayed" ? "#ef4444" : "#2563eb")} 
                    />
                </div>
            </div>

            {/* 11-Stage Interactive Stepper */}
            <div className="onb-stepper-wrap">
                <div className="onb-stepper">
                    {stages.map((st) => {
                        const isFiltered = selectedStageFilter === String(st.id);
                        const isCompleted = st.status === "completed";
                        const isInProgress = st.status === "in_progress";
                        const isDelayed = st.status === "delayed";

                        return (
                            <div 
                                key={st.id} 
                                className={`onb-step-pill ${isFiltered ? "active" : ""} ${isCompleted ? "completed" : ""} ${isDelayed ? "delayed" : ""}`}
                                onClick={() => setSelectedStageFilter(isFiltered ? "all" : String(st.id))}
                            >
                                <div className="onb-step-header">
                                    <span>STAGE {st.id}</span>
                                    {isCompleted ? (
                                        <CheckCircleOutlined style={{ color: "#16a34a" }} />
                                    ) : isDelayed ? (
                                        <WarningOutlined style={{ color: "#dc2626" }} />
                                    ) : isInProgress ? (
                                        <ClockCircleOutlined style={{ color: "#2563eb" }} />
                                    ) : (
                                        <span style={{ color: "#94a3b8" }}>•</span>
                                    )}
                                </div>
                                <div className="onb-step-name" title={st.name}>
                                    {st.name}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Active Stage Filter Helper Banner */}
            {selectedStageFilter !== "all" && (
                <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "10px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: "#1d4ed8" }}>
                        Filtering by <strong>Stage {selectedStageFilter}: {stages.find(s => s.id === parseInt(selectedStageFilter, 10))?.name}</strong>
                    </span>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <Select
                            size="small"
                            value={stages.find(s => s.id === parseInt(selectedStageFilter, 10))?.status || "pending"}
                            onChange={(val) => handleStageStatusChange(selectedStageFilter, val)}
                            style={{ width: 130 }}
                            options={[
                                { label: "Pending", value: "pending" },
                                { label: "In Progress", value: "in_progress" },
                                { label: "Completed", value: "completed" },
                                { label: "Delayed", value: "delayed" }
                            ]}
                        />
                        <Button size="small" onClick={() => setSelectedStageFilter("all")}>Show All Stages</Button>
                    </div>
                </div>
            )}

            {/* Main Tabs Workspace */}
            <div style={{ background: "#ffffff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "20px" }}>
                <Tabs 
                    activeKey={activeTab} 
                    onChange={setActiveTab}
                    items={[
                        {
                            key: "checklist",
                            label: (
                                <span>
                                    <i className="fas fa-tasks mr-1"></i> Checklist & Tasks ({displayedTasks.length})
                                </span>
                            ),
                            children: (
                                <div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                                        <span style={{ fontSize: 13, color: "#64748b" }}>
                                            Check off completed tasks to advance the client's onboarding progress.
                                        </span>
                                        <Button 
                                            icon={<PlusOutlined />} 
                                            size="small" 
                                            onClick={() => setIsTaskModalOpen(true)}
                                        >
                                            Add Client Task
                                        </Button>
                                    </div>

                                    {/* Select All Checkboxes Bar */}
                                    {displayedTasks.length > 0 && (
                                        <div style={{ 
                                            display: "flex", 
                                            justifyContent: "space-between", 
                                            alignItems: "center", 
                                            background: "#f8fafc", 
                                            padding: "10px 16px", 
                                            borderRadius: 8, 
                                            border: "1px solid #e2e8f0", 
                                            marginBottom: 14 
                                        }}>
                                            <Checkbox
                                                checked={displayedTasks.every(t => t.completed)}
                                                indeterminate={displayedTasks.some(t => t.completed) && !displayedTasks.every(t => t.completed)}
                                                onChange={(e) => handleToggleAll(e.target.checked)}
                                                style={{ fontWeight: 600, color: "#0f172a", fontSize: 13 }}
                                            >
                                                {displayedTasks.every(t => t.completed) 
                                                    ? "Deselect All Checkboxes in this Stage" 
                                                    : selectedStageFilter !== "all" 
                                                        ? `Select All in Stage ${selectedStageFilter} (${displayedTasks.length} tasks)`
                                                        : `Select All Tasks (${displayedTasks.length} tasks)`}
                                            </Checkbox>
                                            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>
                                                {displayedTasks.filter(t => t.completed).length} of {displayedTasks.length} tasks completed
                                            </div>
                                        </div>
                                    )}

                                    {displayedTasks.map((t) => {
                                        const isDone = t.completed;
                                        const stageName = stages.find(s => s.id === t.stage_id)?.name || `Stage ${t.stage_id}`;

                                        return (
                                            <div key={t.id} className={`onb-task-item ${isDone ? "done" : ""}`}>
                                                <div className="onb-task-left">
                                                    <Checkbox 
                                                        checked={isDone} 
                                                        onChange={() => handleToggleTask(t)}
                                                    />
                                                    <div>
                                                        <div className={`onb-task-title ${isDone ? "done" : ""}`}>
                                                            {t.title}
                                                        </div>
                                                        <div className="onb-task-chips" style={{ marginTop: 4 }}>
                                                            <span className="onb-chip" style={{ background: "#f1f5f9", color: "#334155" }}>
                                                                Stage {t.stage_id}: {stageName}
                                                            </span>
                                                            <span className="onb-chip owner">
                                                                <UserOutlined /> {t.owner || "Unassigned"}
                                                            </span>
                                                            {t.due_date && (
                                                                <span className="onb-chip date">
                                                                    <CalendarOutlined /> Due: {t.due_date}
                                                                </span>
                                                            )}
                                                            {t.is_custom && (
                                                                <span className="onb-chip custom">Client Specific</span>
                                                            )}
                                                            {t.dependencies && t.dependencies.length > 0 && (
                                                                <span className="onb-chip dep">
                                                                    <WarningOutlined /> Blocked by: {t.dependencies.join(", ")}
                                                                </span>
                                                            )}
                                                            {isDone && t.completed_by && (
                                                                <span style={{ fontSize: 11, color: "#16a34a", marginLeft: 6 }}>
                                                                    ✓ Done by {t.completed_by}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {t.is_custom && (
                                                    <Popconfirm
                                                        title="Delete Task"
                                                        description="Remove this client task?"
                                                        onConfirm={() => handleDeleteTask(t.id)}
                                                    >
                                                        <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                                                    </Popconfirm>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )
                        },
                        {
                            key: "delays",
                            label: (
                                <span>
                                    <WarningOutlined style={{ color: delays.length > 0 ? "#dc2626" : undefined }} /> 
                                    Delay Reasons ({delays.length})
                                </span>
                            ),
                            children: (
                                <div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                                        <span style={{ fontSize: 13, color: "#64748b" }}>
                                            Record root causes of timeline slippages and technical blockers.
                                        </span>
                                        <Button danger icon={<WarningOutlined />} onClick={() => setIsDelayModalOpen(true)}>
                                            Log Delay Reason
                                        </Button>
                                    </div>

                                    {delays.length === 0 ? (
                                        <Alert 
                                            message="No Active Delays" 
                                            description="This onboarding is currently on track with no reported roadblocks." 
                                            type="success" 
                                            showIcon 
                                        />
                                    ) : (
                                        delays.map((del) => (
                                            <div key={del.id} style={{ background: "#fef2f2", border: "1px solid #fee2e2", borderRadius: 8, padding: 16, marginBottom: 12 }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                    <div>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                            <Tag color="error">{del.reason_category}</Tag>
                                                            <span style={{ fontWeight: 600, color: "#991b1b" }}>{del.stage_name}</span>
                                                        </div>
                                                        <p style={{ marginTop: 8, color: "#374151", fontSize: 14, marginBottom: 4 }}>
                                                            {del.description}
                                                        </p>
                                                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                                                            Reported by {del.reported_by} on {dayjs(del.reported_at).format("YYYY-MM-DD HH:mm")}
                                                        </div>
                                                    </div>
                                                    <Popconfirm
                                                        title="Resolve Delay"
                                                        description="Has this blocker been resolved?"
                                                        onConfirm={() => handleDeleteDelay(del.id)}
                                                        okText="Yes, Resolved"
                                                    >
                                                        <Button size="small" type="primary" ghost>Resolve</Button>
                                                    </Popconfirm>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )
                        },
                        {
                            key: "diary",
                            label: (
                                <span>
                                    <i className="fas fa-book-open mr-1"></i> Meeting Diary ({meetingNotes.length})
                                </span>
                            ),
                            children: (
                                <div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                                        <span style={{ fontSize: 13, color: "#64748b" }}>
                                            Operational diary entries for client kickoff calls, technical alignments, and weekly syncs.
                                        </span>
                                        <Button icon={<CalendarOutlined />} onClick={() => setIsMeetingModalOpen(true)}>
                                            Add Diary Entry
                                        </Button>
                                    </div>

                                    {meetingNotes.length === 0 ? (
                                        <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                                            No meeting notes logged yet. Click "Add Diary Entry" to log kickoff minutes.
                                        </div>
                                    ) : (
                                        meetingNotes.map((m) => (
                                            <div key={m.id} className="onb-diary-note">
                                                <div className="onb-diary-header">
                                                    <div>
                                                        <div className="onb-diary-title">{m.title}</div>
                                                        <div className="onb-diary-meta">
                                                            <span><CalendarOutlined /> {m.date}</span>
                                                            <span>•</span>
                                                            <span><UserOutlined /> Attendees: {m.attendees || "Not specified"}</span>
                                                            <span>•</span>
                                                            <span>By {m.created_by}</span>
                                                        </div>
                                                    </div>
                                                    <Popconfirm
                                                        title="Delete Note"
                                                        description="Remove this diary entry?"
                                                        onConfirm={() => handleDeleteMeeting(m.id)}
                                                    >
                                                        <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                                                    </Popconfirm>
                                                </div>

                                                <div className="onb-diary-body">{m.notes}</div>

                                                {m.action_items && (
                                                    <div className="onb-diary-actions">
                                                        <strong>Action Items:</strong> {m.action_items}
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )
                        },
                        /*
                        {
                            key: "docs",
                            label: (
                                <span>
                                    <UploadOutlined /> Documents ({documents.length})
                                </span>
                            ),
                            children: (
                                <div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                                        <span style={{ fontSize: 13, color: "#64748b" }}>
                                            Signed contracts, questionnaires, data mapping spreadsheets, and acceptance certificates.
                                        </span>
                                        <Button icon={<UploadOutlined />} onClick={() => setIsDocModalOpen(true)}>
                                            Attach Document
                                        </Button>
                                    </div>

                                    {documents.length === 0 ? (
                                        <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                                            No documents attached yet.
                                        </div>
                                    ) : (
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
                                            {documents.map((d) => (
                                                <div key={d.id} style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, background: "#f8fafc" }}>
                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                        <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 14 }}>
                                                            <i className="fas fa-file-pdf text-danger mr-2"></i> {d.title}
                                                        </div>
                                                        <Popconfirm
                                                            title="Delete Document"
                                                            description="Remove this document link?"
                                                            onConfirm={() => handleDeleteDoc(d.id)}
                                                        >
                                                            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                                                        </Popconfirm>
                                                    </div>
                                                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
                                                        Stage {d.stage_id} • Uploaded by {d.uploaded_by} on {dayjs(d.uploaded_at).format("YYYY-MM-DD")}
                                                    </div>
                                                    {d.file_url && (
                                                        <div style={{ marginTop: 12 }}>
                                                            <a href={d.file_url} target="_blank" rel="noopener noreferrer">
                                                                <Button size="small" type="primary" ghost>Download File</Button>
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )
                        },
                        */
                        {
                            key: "approvals",
                            label: (
                                <span>
                                    <CheckCircleOutlined /> Go-Live & Approvals
                                </span>
                            ),
                            children: (
                                <div>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                                        {/* Go-Live Card */}
                                        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 20, background: "#ffffff" }}>
                                            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                                                <i className="fas fa-rocket text-primary mr-2"></i> Go-Live Approval
                                            </h3>
                                            <p style={{ fontSize: 13, color: "#64748b", margin: "6px 0 16px 0" }}>
                                                Formal implementation lead sign-off verifying that Stages 1-9 are ready for production traffic.
                                            </p>

                                            {approvals.go_live?.approved ? (
                                                <Alert 
                                                    message="Go-Live Approved" 
                                                    description={`Approved by ${approvals.go_live.approved_by} on ${dayjs(approvals.go_live.approved_at).format("YYYY-MM-DD HH:mm")}. Remarks: "${approvals.go_live.remarks || 'None'}"`}
                                                    type="success" 
                                                    showIcon 
                                                />
                                            ) : !isReadyForGoLive ? (
                                                <div>
                                                    <Alert 
                                                        message="🔒 Go-Live Approval Locked" 
                                                        description={
                                                            <div>
                                                                Stages 1 through 9 must be completed before Go-Live can be approved.
                                                                <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                                                                    <strong>Pending Stages ({pendingPreFlightStages.length}):</strong>{" "}
                                                                    {pendingPreFlightStages.map(s => `Stage ${s.id} (${s.name})`).join(", ")}
                                                                </div>
                                                            </div>
                                                        }
                                                        type="warning" 
                                                        showIcon 
                                                        style={{ marginBottom: 16 }}
                                                    />
                                                    <Button 
                                                        disabled 
                                                        icon={<LockOutlined />}
                                                        style={{ background: "#f1f5f9", borderColor: "#cbd5e1", color: "#94a3b8" }}
                                                    >
                                                        Approve Go-Live (Locked)
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div>
                                                    <Alert 
                                                        message="✅ Ready for Go-Live Sign-Off" 
                                                        description="All pre-flight stages (1 to 9) are completed. You can now record official Go-Live approval." 
                                                        type="info" 
                                                        showIcon 
                                                        style={{ marginBottom: 16 }}
                                                    />
                                                    <Button 
                                                        type="primary" 
                                                        style={{ background: "#16a34a", borderColor: "#16a34a" }}
                                                        onClick={() => {
                                                            setApprovalType("go_live");
                                                            setIsApprovalModalOpen(true);
                                                        }}
                                                    >
                                                        Approve Go-Live
                                                    </Button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Client Acceptance Card */}
                                        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 20, background: "#ffffff" }}>
                                            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                                                <i className="fas fa-handshake text-success mr-2"></i> Client Acceptance
                                            </h3>
                                            <p style={{ fontSize: 13, color: "#64748b", margin: "6px 0 16px 0" }}>
                                                Formal acknowledgment from the client stakeholder accepting delivery of configured modules.
                                            </p>

                                            {approvals.client_acceptance?.accepted ? (
                                                <Alert 
                                                    message="Client Acceptance Confirmed" 
                                                    description={`Signed off by ${approvals.client_acceptance.accepted_by} on ${dayjs(approvals.client_acceptance.accepted_at).format("YYYY-MM-DD HH:mm")}. Remarks: "${approvals.client_acceptance.remarks || 'None'}"`}
                                                    type="success" 
                                                    showIcon 
                                                />
                                            ) : !approvals.go_live?.approved ? (
                                                <div>
                                                    <Alert 
                                                        message="🔒 Client Acceptance Locked" 
                                                        description="Go-Live must be approved first before recording final client acceptance." 
                                                        type="info" 
                                                        showIcon 
                                                        style={{ marginBottom: 16 }}
                                                    />
                                                    <Button 
                                                        disabled 
                                                        icon={<LockOutlined />}
                                                        style={{ background: "#f1f5f9", borderColor: "#cbd5e1", color: "#94a3b8" }}
                                                    >
                                                        Record Client Acceptance (Locked)
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div>
                                                    <Alert 
                                                        message="Pending Client Sign-Off" 
                                                        description="Record client acceptance once user training and hypercare kick off." 
                                                        type="warning" 
                                                        showIcon 
                                                        style={{ marginBottom: 16 }}
                                                    />
                                                    <Button 
                                                        type="primary" 
                                                        onClick={() => {
                                                            setApprovalType("client_acceptance");
                                                            setIsApprovalModalOpen(true);
                                                        }}
                                                    >
                                                        Record Client Acceptance
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                    ]}
                />
            </div>

            {/* Modal: Add Delay */}
            <Modal
                title={<div style={{ color: "#dc2626" }}><WarningOutlined /> Log Onboarding Delay Reason</div>}
                open={isDelayModalOpen}
                onCancel={() => setIsDelayModalOpen(false)}
                footer={null}
                destroyOnClose
            >
                <Form form={delayForm} layout="vertical" onFinish={handleCreateDelay}>
                    <Form.Item name="stage_id" label="Affected Stage" initialValue={1} rules={[{ required: true }]}>
                        <Select options={stages.map(s => ({ label: `Stage ${s.id}: ${s.name}`, value: s.id }))} />
                    </Form.Item>
                    <Form.Item name="reason_category" label="Delay Category" initialValue="Client IT / SSO / Firewall Access" rules={[{ required: true }]}>
                        <Select options={DELAY_CATEGORIES.map(c => ({ label: c, value: c }))} />
                    </Form.Item>
                    <Form.Item name="description" label="Detailed Explanation & Impact" rules={[{ required: true, message: "Please describe the delay" }]}>
                        <Input.TextArea rows={3} placeholder="Explain what is blocking progress and expected resolution timeline..." />
                    </Form.Item>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                        <Button onClick={() => setIsDelayModalOpen(false)}>Cancel</Button>
                        <Button type="primary" danger htmlType="submit">Log Delay</Button>
                    </div>
                </Form>
            </Modal>

            {/* Modal: Add Meeting / Diary Note */}
            <Modal
                title={<div><CalendarOutlined /> Add Implementation Diary Note</div>}
                open={isMeetingModalOpen}
                onCancel={() => setIsMeetingModalOpen(false)}
                footer={null}
                destroyOnClose
            >
                <Form form={meetingForm} layout="vertical" onFinish={handleCreateMeeting}>
                    <Form.Item name="title" label="Call / Meeting Title" initialValue="Weekly Onboarding Alignment" rules={[{ required: true }]}>
                        <Input placeholder="e.g. Kickoff Call, Branding Alignment, Data Review" />
                    </Form.Item>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <Form.Item name="date" label="Meeting Date" initialValue={dayjs()}>
                            <DatePicker style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item name="attendees" label="Attendees">
                            <Input placeholder="e.g. Sarah (Client PM), Alex, Tejas" />
                        </Form.Item>
                    </div>
                    <Form.Item name="notes" label="Meeting Minutes & Discussion Points" rules={[{ required: true, message: "Enter notes" }]}>
                        <Input.TextArea rows={4} placeholder="Key decisions made, items discussed..." />
                    </Form.Item>
                    <Form.Item name="action_items" label="Next Action Items">
                        <Input placeholder="e.g. Client to send updated CSV by Friday" />
                    </Form.Item>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                        <Button onClick={() => setIsMeetingModalOpen(false)}>Cancel</Button>
                        <Button type="primary" htmlType="submit" style={{ background: "#2563eb" }}>Save Diary Entry</Button>
                    </div>
                </Form>
            </Modal>

            {/* Modal: Add Custom Task */}
            <Modal
                title={<div><PlusOutlined /> Add Client-Specific Task</div>}
                open={isTaskModalOpen}
                onCancel={() => setIsTaskModalOpen(false)}
                footer={null}
                destroyOnClose
            >
                <Form form={taskForm} layout="vertical" onFinish={handleCreateTask}>
                    <Form.Item name="title" label="Task Title" rules={[{ required: true, message: "Enter task title" }]}>
                        <Input placeholder="e.g. Set up custom SAML 2.0 Okta integration" />
                    </Form.Item>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <Form.Item name="stage_id" label="Stage" initialValue={parseInt(selectedStageFilter, 10) || 1} rules={[{ required: true }]}>
                            <Select options={stages.map(s => ({ label: `Stage ${s.id}: ${s.name}`, value: s.id }))} />
                        </Form.Item>
                        <Form.Item name="owner" label="Task Owner" initialValue="Implementation Lead">
                            <Input placeholder="e.g. Client IT, CARA Dev" />
                        </Form.Item>
                    </div>
                    <Form.Item name="due_date" label="Target Due Date">
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item name="notes" label="Notes / Instructions">
                        <Input.TextArea rows={2} placeholder="Optional technical details or checklist steps" />
                    </Form.Item>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                        <Button onClick={() => setIsTaskModalOpen(false)}>Cancel</Button>
                        <Button type="primary" htmlType="submit" style={{ background: "#2563eb" }}>Add Task</Button>
                    </div>
                </Form>
            </Modal>

            {/* Modal: Upload Document */}
            <Modal
                title={<div><UploadOutlined /> Attach Document to Onboarding</div>}
                open={isDocModalOpen}
                onCancel={() => setIsDocModalOpen(false)}
                footer={null}
                destroyOnClose
            >
                <Form form={docForm} layout="vertical" onFinish={handleUploadDoc}>
                    <Form.Item name="title" label="Document Title" rules={[{ required: true, message: "Enter title" }]}>
                        <Input placeholder="e.g. Signed_SOW_2026.pdf" />
                    </Form.Item>
                    <Form.Item name="stage_id" label="Associated Stage" initialValue={1}>
                        <Select options={stages.map(s => ({ label: `Stage ${s.id}: ${s.name}`, value: s.id }))} />
                    </Form.Item>
                    <Form.Item label="Upload File">
                        <Upload 
                            beforeUpload={() => false} 
                            fileList={fileList} 
                            onChange={({ fileList }) => setFileList(fileList)}
                            maxCount={1}
                        >
                            <Button icon={<UploadOutlined />}>Select Local File</Button>
                        </Upload>
                    </Form.Item>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
                        <Button onClick={() => setIsDocModalOpen(false)}>Cancel</Button>
                        <Button type="primary" htmlType="submit" style={{ background: "#2563eb" }}>Attach Document</Button>
                    </div>
                </Form>
            </Modal>

            {/* Modal: Approvals */}
            <Modal
                title={<div><CheckCircleOutlined /> {approvalType === 'go_live' ? 'Approve Go-Live' : 'Record Client Acceptance'}</div>}
                open={isApprovalModalOpen}
                onCancel={() => setIsApprovalModalOpen(false)}
                footer={null}
                destroyOnClose
            >
                <Form form={approvalForm} layout="vertical" onFinish={handleSubmitApproval}>
                    <Form.Item name="approver_name" label="Sign-Off Approver Name" initialValue="Superadmin" rules={[{ required: true }]}>
                        <Input placeholder="e.g. Alex Johnson (Lead IPM)" />
                    </Form.Item>
                    <Form.Item name="remarks" label="Sign-off Remarks / Notes">
                        <Input.TextArea rows={3} placeholder="e.g. All pre-flight tests completed, training confirmed, approved for launch." />
                    </Form.Item>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                        <Button onClick={() => setIsApprovalModalOpen(false)}>Cancel</Button>
                        <Button type="primary" htmlType="submit" style={{ background: "#16a34a", borderColor: "#16a34a" }}>
                            Confirm Sign-Off
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/* Modal: Implementation Status Report (Printable) */}
            <Modal
                title={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", paddingRight: 32 }}>
                        <span>Implementation Status Report</span>
                        <Button icon={<PrinterOutlined />} onClick={() => window.print()} className="no-print">
                            Print / Save PDF
                        </Button>
                    </div>
                }
                open={isReportModalOpen}
                onCancel={() => setIsReportModalOpen(false)}
                width={850}
                footer={null}
            >
                <div className="onb-report-container onb-printable-area">
                    <div className="onb-report-header">
                        <div>
                            <div className="onb-report-brand">CARA GRC Platform</div>
                            <div style={{ color: "#64748b", fontSize: 13 }}>Enterprise Client Onboarding Status Report</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontWeight: 700, fontSize: 16 }}>{tracker.tracker_code}</div>
                            <div style={{ fontSize: 12, color: "#64748b" }}>Generated: {dayjs().format("YYYY-MM-DD HH:mm")}</div>
                        </div>
                    </div>

                    <div className="onb-report-grid">
                        <div><strong>Client Name:</strong> {tracker.client_name}</div>
                        <div><strong>Legal Entity:</strong> {tracker.legal_entity_name}</div>
                        <div><strong>Implementation Lead:</strong> {tracker.lead_owner}</div>
                        <div><strong>Assigned VRM:</strong> {tracker.vrm_assigned}</div>
                        <div><strong>Target Go-Live:</strong> {tracker.target_go_live_date || "N/A"}</div>
                        <div><strong>Actual Go-Live:</strong> {tracker.actual_go_live_date || "Pending"}</div>
                        <div><strong>Current Health:</strong> {tracker.health?.toUpperCase()}</div>
                        <div><strong>Overall Progress:</strong> {progress}%</div>
                    </div>

                    <h4 style={{ fontWeight: 700, marginBottom: 12, color: "#0f172a" }}>Stage-by-Stage Breakdown</h4>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 24 }}>
                        <thead>
                            <tr style={{ background: "#f1f5f9", textAlign: "left" }}>
                                <th style={{ padding: "8px 12px", border: "1px solid #e2e8f0" }}>Stage #</th>
                                <th style={{ padding: "8px 12px", border: "1px solid #e2e8f0" }}>Stage Name</th>
                                <th style={{ padding: "8px 12px", border: "1px solid #e2e8f0" }}>Status</th>
                                <th style={{ padding: "8px 12px", border: "1px solid #e2e8f0" }}>Tasks Done</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stages.map(s => {
                                const sTasks = tracker.tasks.filter(t => t.stage_id === s.id);
                                const doneCount = sTasks.filter(t => t.completed).length;
                                return (
                                    <tr key={s.id}>
                                        <td style={{ padding: "8px 12px", border: "1px solid #e2e8f0" }}>Stage {s.id}</td>
                                        <td style={{ padding: "8px 12px", border: "1px solid #e2e8f0", fontWeight: 500 }}>{s.name}</td>
                                        <td style={{ padding: "8px 12px", border: "1px solid #e2e8f0" }}>
                                            <Tag color={s.status === "completed" ? "success" : (s.status === "delayed" ? "error" : "processing")}>
                                                {s.status.toUpperCase()}
                                            </Tag>
                                        </td>
                                        <td style={{ padding: "8px 12px", border: "1px solid #e2e8f0" }}>
                                            {doneCount} / {sTasks.length} ({sTasks.length > 0 ? Math.round((doneCount / sTasks.length) * 100) : 0}%)
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {delays.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                            <h4 style={{ fontWeight: 700, color: "#dc2626", marginBottom: 8 }}>Active Delays / Escalations</h4>
                            {delays.map(d => (
                                <div key={d.id} style={{ fontSize: 13, background: "#fef2f2", padding: "8px 12px", borderRadius: 6, marginBottom: 6, border: "1px solid #fee2e2" }}>
                                    <strong>[{d.stage_name} - {d.reason_category}]:</strong> {d.description}
                                </div>
                            ))}
                        </div>
                    )}

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 24, borderTop: "1px solid #e2e8f0", paddingTop: 16 }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>GO-LIVE APPROVAL</div>
                            <div style={{ fontSize: 13, marginTop: 4 }}>
                                {approvals.go_live?.approved ? `Approved by ${approvals.go_live.approved_by} on ${dayjs(approvals.go_live.approved_at).format("YYYY-MM-DD")}` : "Pending Approval"}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>CLIENT ACCEPTANCE</div>
                            <div style={{ fontSize: 13, marginTop: 4 }}>
                                {approvals.client_acceptance?.accepted ? `Accepted by ${approvals.client_acceptance.accepted_by} on ${dayjs(approvals.client_acceptance.accepted_at).format("YYYY-MM-DD")}` : "Pending Acceptance"}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default OnboardingDetail;

